// React bootstrap table next =>
// DOCS: https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/
// STORYBOOK: https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html
import React, { useEffect, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationProvider,
} from 'react-bootstrap-table2-paginator';
import { injectIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getHandlerTableChange,
  getSelectRow,
  headerSortingClasses,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
} from '../../../../../_metronic/_helpers';
import { Pagination } from '../../../../../_metronic/_partials/controls';
import * as actions from '../../_redux/usersActions';
import { useUsersUIContext } from '../UsersUIContext';
import * as uiHelpers from '../UsersUIHelpers';
import * as columnFormatters from './column-formatters';
import MessageBox from './../../../../components/MessageBox';

function UsersTable(props) {
  const { intl } = props;
  // Users UI Context
  console.log('Load UsersTable');
  const UsersUIContext = useUsersUIContext();
  const UsersUIProps = useMemo(() => {
    return {
      ids: UsersUIContext.ids,
      setIds: UsersUIContext.setIds,
      queryParams: UsersUIContext.queryParams,
      setQueryParams: UsersUIContext.setQueryParams,
      openEditUserDialog: UsersUIContext.openEditUserDialog,
      openDeleteUserDialog: UsersUIContext.openDeleteUserDialog,
    };
  }, [UsersUIContext]);

  // Getting curret state of Users list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.users }),
    shallowEqual
  );
  const { totalCount, entities, listLoading } = currentState
    ? currentState
    : { totalCount: 0, entities: [], listLoading: false };
  const idLabel = intl.formatMessage({ id: 'USER.LABEL.ID' });
  const nameLabel = intl.formatMessage({ id: 'USER.LABEL.NAME' });
  const emailLabel = intl.formatMessage({ id: 'USER.LABEL.EMAIL' });
  const mobileNoLabel = intl.formatMessage({ id: 'USER.LABEL.MOBILENO' });
  const roleLabel = intl.formatMessage({ id: 'USER.LABEL.ROLE' });
  const statusLabel = intl.formatMessage({ id: 'USER.LABEL.STATUS' });
  const actionsLabel = intl.formatMessage({ id: 'USER.LABEL.ACTIONS' });

  // Users Redux state
  const dispatch = useDispatch();
  useEffect(() => {
    // clear selections list
    UsersUIProps.setIds([]);
    // server call by queryParams
    dispatch(actions.fetchUsers(UsersUIProps.queryParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UsersUIProps.queryParams, dispatch]);

  useEffect(() => {
    if (currentState.error) {
      MessageBox({ icon: 'error', text: currentState.error });
    }
  }, [currentState.error]);

  // Table columns
  const columns = [
    {
      dataField: 'id',
      text: idLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'name',
      text: nameLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'email',
      text: emailLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
    },
    {
      dataField: 'mobileNo',
      text: mobileNoLabel,
      sort: false,
      sortCaret: sortCaret,
    },
    {
      dataField: 'role',
      text: roleLabel,
      sort: true,
      sortCaret: sortCaret,
      headerSortingClasses,
      /* formatter: columnFormatters.RoleColumnFormatter, */
    },
    {
      dataField: 'status',
      text: statusLabel,
      sort: true,
      sortCaret: sortCaret,
      formatter: columnFormatters.StatusColumnFormatter,
      headerSortingClasses,
    },

    {
      dataField: 'action',
      text: actionsLabel,
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        openEditUserDialog: UsersUIProps.openEditUserDialog,
        openDeleteUserDialog: UsersUIProps.openDeleteUserDialog,
      },
      classes: 'text-right pr-0',
      headerClasses: 'text-right pr-3',
      style: {
        minWidth: '100px',
      },
    },
  ];
  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: UsersUIProps.queryParams.pageSize,
    page: UsersUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses='table-responsive'
                bordered={false}
                classes='table table-head-custom table-vertical-center overflow-hidden'
                bootstrap4
                remote
                keyField='id'
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  UsersUIProps.setQueryParams
                )}
                selectRow={getSelectRow({
                  entities,
                  ids: UsersUIProps.ids,
                  setIds: UsersUIProps.setIds,
                })}
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
}

export default injectIntl(UsersTable);
